import React, { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import JobKPICards from "./JobKPICards";
import JobActivityTimeline from "./JobActivityTimeline";
import { JobActivityItem } from "../constants/jobActivityConfig";
import { safeStringToArray } from "../utils/dataHelpers";
import "./JobDetailSidebar.css";

interface AssignedRecruiter {
  recruiter_id: number;
  recruiter_name?: string;
  username?: string;
}

interface Job {
  id: number;
  title: string;
  department?: string;
  location?: string;
  employment_type?: string;
  seniority?: string;
  owner_name?: string;
  customer_name?: string;
  customer_company_name?: string;
  customer_rate?: number;
  engineer_rate?: number;
  skills?: string[] | string;
  responsible_sales_name?: string;
  assigned_recruiters?: AssignedRecruiter[];
  created_at?: string;
  status_changed_at?: string;
}

interface Application {
  id: number;
  status: string;
  ai_match_score?: number;
}

interface Recruiter {
  id: number;
  username: string;
  email: string;
}

interface JobDetailSidebarProps {
  job: Job;
  applications: Application[];
  activityItems: JobActivityItem[];
  activityLoading: boolean;
  recruiters: Recruiter[];
  selectedRecruiterId: string;
  onRecruiterSelect: (id: string) => void;
  onAssignRecruiter: () => void;
  onRemoveRecruiter: (id: number) => void;
  isAssigningRecruiter: boolean;
  removingRecruiterId: number | null;
  canManageAssignments: boolean;
  jobId: number;
}

const JobDetailSidebar: React.FC<JobDetailSidebarProps> = ({
  job,
  applications,
  activityItems,
  activityLoading,
  recruiters,
  selectedRecruiterId,
  onRecruiterSelect,
  onAssignRecruiter,
  onRemoveRecruiter,
  isAssigningRecruiter,
  removingRecruiterId,
  canManageAssignments,
  jobId,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const skills = safeStringToArray(job.skills);

  const infoItems = [
    { label: t("jobs.detail.labels.department"), value: job.department },
    { label: t("jobs.detail.labels.location"), value: job.location },
    {
      label: t("jobs.detail.labels.employmentType"),
      value: job.employment_type,
    },
    { label: t("jobs.detail.labels.seniority"), value: job.seniority },
    {
      label: t("jobs.detail.labels.owner"),
      value: job.owner_name || t("jobs.list.unassigned"),
    },
  ];

  // Add KAM if present
  if (job.responsible_sales_name) {
    infoItems.push({
      label: "KAM",
      value: job.responsible_sales_name,
    });
  }

  // Add customer info if present
  if (job.customer_name) {
    infoItems.push({
      label: t("jobs.detail.labels.customer"),
      value: job.customer_company_name || job.customer_name,
    });
  }
  if (job.customer_rate) {
    infoItems.push({
      label: t("jobs.detail.labels.customerRate"),
      value: `$${job.customer_rate}${t("common:misc.perDay")}`,
    });
  }
  if (job.engineer_rate) {
    infoItems.push({
      label: t("jobs.detail.labels.engineerRate"),
      value: `$${job.engineer_rate}${t("common:misc.perDay")}`,
    });
  }

  return (
    <div className="job-detail-sidebar">
      {/* Job Info Card */}
      <div className="sidebar-card">
        <h4 className="sidebar-card-title">
          {t("jobs.detail.sections.jobInformation")}
        </h4>
        <div className="sidebar-info-list">
          {infoItems.map((item) => (
            <div key={item.label} className="sidebar-info-item">
              <span className="sidebar-info-label">{item.label}</span>
              <span className="sidebar-info-value">
                {item.value || t("jobs.detail.labels.notSpecified")}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="sidebar-card">
        <h4 className="sidebar-card-title">
          {t("jobs.detail.sections.keyMetrics")}
        </h4>
        <JobKPICards job={job} applications={applications} />
      </div>

      {/* Skills */}
      {skills.length > 0 && (
        <div className="sidebar-card">
          <h4 className="sidebar-card-title">
            {t("jobs.detail.sections.requiredSkills")}
          </h4>
          <div className="sidebar-skills-list">
            {skills.map((skill) => (
              <span key={skill} className="sidebar-skill-tag">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Recruiter Assignments */}
      <div className="sidebar-card">
        <h4 className="sidebar-card-title">
          {t("jobs.detail.sections.recruiterAssignments")}
        </h4>
        <div className="sidebar-recruiter-list">
          {job.assigned_recruiters && job.assigned_recruiters.length > 0 ? (
            job.assigned_recruiters.map((recruiter) => (
              <span key={recruiter.recruiter_id} className="recruiter-chip">
                {recruiter.recruiter_name ||
                  recruiter.username ||
                  t("jobs.detail.recruiterAssignments.recruiter")}
                {canManageAssignments && (
                  <button
                    className="chip-remove"
                    onClick={() => onRemoveRecruiter(recruiter.recruiter_id)}
                    disabled={removingRecruiterId === recruiter.recruiter_id}
                    aria-label={`${t("common:actions.remove")} ${recruiter.recruiter_name || recruiter.username || ""}`}
                  >
                    &times;
                  </button>
                )}
              </span>
            ))
          ) : (
            <p className="sidebar-muted">
              {t("jobs.detail.recruiterAssignments.noRecruiters")}
            </p>
          )}
        </div>
        {canManageAssignments && (
          <div className="sidebar-assign-row">
            <select
              value={selectedRecruiterId}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                onRecruiterSelect(e.target.value)
              }
              className="sidebar-select"
            >
              <option value="">
                {t("jobs.detail.recruiterAssignments.selectRecruiter")}
              </option>
              {recruiters
                .filter(
                  (r) =>
                    !(job.assigned_recruiters || []).some(
                      (ar) => ar.recruiter_id === r.id,
                    ),
                )
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.username} ({r.email})
                  </option>
                ))}
            </select>
            <button
              className="btn-primary sidebar-assign-btn"
              onClick={onAssignRecruiter}
              disabled={!selectedRecruiterId || isAssigningRecruiter}
            >
              {isAssigningRecruiter
                ? t("jobs.detail.recruiterAssignments.assigning")
                : t("jobs.detail.recruiterAssignments.assignRecruiter")}
            </button>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="sidebar-card">
        <h4 className="sidebar-card-title">
          {t("jobs.detail.sections.recentActivity")}
        </h4>
        <JobActivityTimeline
          activities={activityItems.slice(0, 5)}
          isLoading={activityLoading}
          variant="summary"
          jobId={jobId}
        />
      </div>
    </div>
  );
};

export default JobDetailSidebar;
